import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import {
  Building2, FileText, LogOut, ChevronRight, Download, Clock,
  CheckCircle2, HardHat, Euro, Receipt, Plus, Send, MessageSquare,
  AlertTriangle, MapPin, Calendar, Eye, X, Loader2, Home,
  ClipboardList, Briefcase, User, Lock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ==========================================
// AUTH CONTEXT
// ==========================================
const PortalAuthContext = createContext(null);

function usePortalAuth() {
  return useContext(PortalAuthContext);
}

// ==========================================
// API HELPER
// ==========================================
function portalApi() {
  const token = localStorage.getItem('portal_token');
  return axios.create({
    baseURL: `${API}/api/portail`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true
  });
}

// ==========================================
// STAGES
// ==========================================
const STAGES = [
  { id: 'devis', label: 'Devis envoyé', icon: FileText, color: 'blue' },
  { id: 'accepte', label: 'Accepté', icon: CheckCircle2, color: 'green' },
  { id: 'bon_commande', label: 'Bon de commande', icon: Receipt, color: 'purple' },
  { id: 'chantier', label: 'Travaux en cours', icon: HardHat, color: 'amber' },
  { id: 'facture', label: 'Terminé & Facturé', icon: Euro, color: 'emerald' },
];

// ==========================================
// LOGIN PAGE
// ==========================================
function PortalLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/portail/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem('portal_token', res.data.token);
      onLogin(res.data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erreur de connexion');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-white">Reno Resolve</h1>
              <p className="text-slate-400 text-sm">Portail Clients</p>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Bienvenue</h2>
          <p className="text-slate-500 text-sm mb-6">Connectez-vous pour accéder à vos projets</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                  placeholder="votre@email.fr"
                  required
                  data-testid="portal-email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                  placeholder="Votre mot de passe"
                  required
                  data-testid="portal-password-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="portal-login-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Se connecter
            </button>
          </form>
        </div>

        <p className="text-center text-slate-500 text-xs mt-6">
          Accès réservé aux clients de Reno Resolve
        </p>
      </div>
    </div>
  );
}

// ==========================================
// PORTAL DASHBOARD
// ==========================================
function PortalDashboard() {
  const { user, logout } = usePortalAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard'); // dashboard, devis_detail, new_demande
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [devisDetail, setDevisDetail] = useState(null);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await portalApi().get('/dashboard');
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const loadDevisDetail = async (devisId) => {
    try {
      const res = await portalApi().get(`/devis/${devisId}`);
      setDevisDetail(res.data);
      setView('devis_detail');
    } catch (e) {
      console.error(e);
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Nav */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-green-500 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-900 text-sm">Reno Resolve</p>
              <p className="text-xs text-slate-500">{data.organisation}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {view !== 'dashboard' && (
              <button onClick={() => setView('dashboard')} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
                <Home className="w-4 h-4" /> Tableau de bord
              </button>
            )}
            <div className="text-right">
              <p className="text-sm font-medium text-slate-700">{user.nom}</p>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
            <button onClick={logout} className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors" data-testid="portal-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {view === 'dashboard' && (
          <DashboardView
            data={data}
            onViewDevis={loadDevisDetail}
            onNewDemande={() => setView('new_demande')}
            onReload={loadDashboard}
          />
        )}
        {view === 'devis_detail' && devisDetail && (
          <DevisDetailView
            devis={devisDetail}
            onBack={() => setView('dashboard')}
          />
        )}
        {view === 'new_demande' && (
          <NewDemandeView
            onBack={() => { setView('dashboard'); loadDashboard(); }}
          />
        )}
      </div>
    </div>
  );
}

// ==========================================
// DASHBOARD VIEW
// ==========================================
function DashboardView({ data, onViewDevis, onNewDemande, onReload }) {
  const { stats, devis, demandes } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tableau de bord</h1>
          <p className="text-slate-500">Suivez vos projets et devis</p>
        </div>
        <button
          onClick={onNewDemande}
          className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
          data-testid="new-demande-btn"
        >
          <Plus className="w-4 h-4" />
          Nouvelle demande
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total devis', value: stats.total_devis, icon: FileText, color: 'blue' },
          { label: 'Devis acceptés', value: stats.devis_acceptes, icon: CheckCircle2, color: 'green' },
          { label: 'Chantiers en cours', value: stats.chantiers_en_cours, icon: HardHat, color: 'amber' },
          { label: 'Demandes ouvertes', value: stats.demandes_ouvertes, icon: MessageSquare, color: 'purple' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg bg-${s.color}-100`}>
                <s.icon className={`w-5 h-5 text-${s.color}-600`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                <p className="text-slate-500 text-sm">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Devis List */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            Vos devis et projets
          </h2>
        </div>
        <div className="divide-y divide-slate-100">
          {devis.length > 0 ? devis.map((d, i) => {
            const stageIdx = STAGES.findIndex(s => s.id === d.stage);
            return (
              <div
                key={i}
                onClick={() => onViewDevis(d.devis_id)}
                className="p-4 hover:bg-slate-50 cursor-pointer transition-colors flex items-center justify-between"
                data-testid={`portal-devis-${d.devis_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    d.statut === 'Accepté' ? 'bg-green-100' : d.statut === 'Refusé' ? 'bg-red-100' : 'bg-blue-100'
                  }`}>
                    <FileText className={`w-5 h-5 ${
                      d.statut === 'Accepté' ? 'text-green-600' : d.statut === 'Refusé' ? 'text-red-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{d.devis_id}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        d.statut === 'Accepté' ? 'bg-green-100 text-green-700' :
                        d.statut === 'Refusé' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{d.statut}</span>
                    </div>
                    <p className="text-sm text-slate-500">{d.project_title || 'Sans titre'}</p>
                    {d.chantier_adresse && (
                      <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {d.chantier_adresse}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  {/* Mini pipeline */}
                  <div className="flex items-center gap-1">
                    {STAGES.map((stage, si) => (
                      <div key={si} className={`w-2 h-2 rounded-full ${si <= stageIdx ? 'bg-green-500' : 'bg-slate-200'}`} />
                    ))}
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-900">{d.montant_ttc?.toLocaleString('fr-FR')}€ TTC</p>
                    <p className="text-xs text-slate-400">
                      {d.date_creation ? new Date(d.date_creation).toLocaleDateString('fr-FR') : ''}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            );
          }) : (
            <div className="p-8 text-center text-slate-400">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>Aucun devis</p>
            </div>
          )}
        </div>
      </div>

      {/* Demandes */}
      {demandes.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-purple-500" />
              Vos demandes
            </h2>
          </div>
          <div className="divide-y divide-slate-100">
            {demandes.map((d, i) => (
              <div key={i} className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-slate-900">{d.titre}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    d.statut === 'ouverte' ? 'bg-blue-100 text-blue-700' :
                    d.statut === 'en_cours' ? 'bg-amber-100 text-amber-700' :
                    'bg-green-100 text-green-700'
                  }`}>{d.statut === 'ouverte' ? 'Ouverte' : d.statut === 'en_cours' ? 'En cours' : 'Fermée'}</span>
                </div>
                <p className="text-sm text-slate-500">{d.description}</p>
                {d.reponse && (
                  <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-xs text-green-600 font-medium mb-1">Réponse de Reno Resolve :</p>
                    <p className="text-sm text-slate-700">{d.reponse}</p>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-2">
                  {d.created_at ? new Date(d.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// DEVIS DETAIL VIEW
// ==========================================
function DevisDetailView({ devis, onBack }) {
  const stageIdx = STAGES.findIndex(s => {
    if (devis.lifecycle?.facture_numero) return s.id === 'facture';
    if (devis.lifecycle?.bon_commande) return s.id === 'bon_commande';
    if (devis.statut === 'Accepté') return s.id === 'accepte';
    return s.id === 'devis';
  });
  const actualStageIdx = devis.lifecycle?.facture_numero ? 4 :
    devis.lifecycle?.bon_commande ? 2 :
    devis.statut === 'Accepté' ? 1 : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
          <Home className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-slate-900">{devis.devis_id}</h2>
          <p className="text-slate-500">{devis.project_title}</p>
        </div>
        <button
          onClick={() => window.open(`${API}/api/devis/${devis.devis_id}/pdf`, '_blank')}
          className="ml-auto px-4 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 flex items-center gap-2 text-sm font-medium"
        >
          <Download className="w-4 h-4" /> Télécharger le devis PDF
        </button>
      </div>

      {/* Pipeline */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-sm font-medium text-slate-500 mb-4">Avancement du projet</h3>
        <div className="flex items-center justify-between relative">
          <div className="absolute top-5 left-0 right-0 h-1 bg-slate-100 z-0 rounded-full" />
          <div className="absolute top-5 left-0 h-1 bg-green-500 z-0 rounded-full transition-all duration-500"
            style={{ width: `${Math.max(0, actualStageIdx) / (STAGES.length - 1) * 100}%` }}
          />
          {STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            const active = idx <= actualStageIdx;
            const current = idx === actualStageIdx;
            return (
              <div key={stage.id} className="flex flex-col items-center z-10 relative">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  current ? 'bg-green-500 border-green-500 text-white scale-110 shadow-lg shadow-green-200' :
                  active ? 'bg-green-500 border-green-500 text-white' :
                  'bg-white border-slate-200 text-slate-400'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`mt-2 text-xs font-medium text-center ${active ? 'text-green-700' : 'text-slate-400'}`}>
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-5 border border-slate-200">
          <p className="text-xs text-slate-400 mb-1">Montant HT</p>
          <p className="text-2xl font-bold text-slate-900">{devis.montant_ht?.toLocaleString('fr-FR')}€</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200">
          <p className="text-xs text-slate-400 mb-1">Montant TTC</p>
          <p className="text-2xl font-bold text-green-700">{devis.montant_ttc?.toLocaleString('fr-FR')}€</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200">
          <p className="text-xs text-slate-400 mb-1">Statut</p>
          <p className={`text-2xl font-bold ${
            devis.statut === 'Accepté' ? 'text-green-600' : devis.statut === 'Refusé' ? 'text-red-600' : 'text-amber-600'
          }`}>{devis.statut}</p>
        </div>
      </div>

      {/* Line items */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Détail des prestations</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-4 text-slate-600 font-medium">Description</th>
              <th className="text-center p-4 text-slate-600 font-medium w-20">Qté</th>
              <th className="text-right p-4 text-slate-600 font-medium w-28">Prix unit.</th>
              <th className="text-right p-4 text-slate-600 font-medium w-28">Total HT</th>
            </tr>
          </thead>
          <tbody>
            {devis.lignes?.map((l, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-4 text-slate-700">{l.description}</td>
                <td className="p-4 text-center text-slate-600">{l.quantite} {l.unite}</td>
                <td className="p-4 text-right text-slate-600">{l.prix_unitaire?.toLocaleString('fr-FR')}€</td>
                <td className="p-4 text-right font-medium">{(l.total_ht || l.prix_unitaire * l.quantite)?.toLocaleString('fr-FR')}€</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Adresse chantier */}
      {devis.chantier_adresse && (
        <div className="bg-white rounded-xl p-5 border border-slate-200">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-slate-400" />
            Adresse du chantier
          </h3>
          <p className="text-slate-700">{devis.chantier_adresse}</p>
        </div>
      )}

      {/* Documents */}
      {(devis.lifecycle?.bon_commande || devis.lifecycle?.facture_numero) && (
        <div className="bg-white rounded-xl p-5 border border-slate-200">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
            <ClipboardList className="w-4 h-4 text-slate-400" />
            Documents
          </h3>
          <div className="space-y-2">
            {devis.lifecycle?.bon_commande && (
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <Receipt className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-slate-900">Bon de commande</p>
                  <p className="text-xs text-slate-500">Réf: {devis.lifecycle.bon_commande.reference || '-'}</p>
                </div>
              </div>
            )}
            {devis.lifecycle?.facture_numero && (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <Euro className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-slate-900">Facture {devis.lifecycle.facture_numero}</p>
                  <p className="text-xs text-slate-500">{devis.lifecycle.facture_date ? new Date(devis.lifecycle.facture_date).toLocaleDateString('fr-FR') : ''}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// NEW DEMANDE VIEW
// ==========================================
function NewDemandeView({ onBack }) {
  const [type, setType] = useState('libre');
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [adresse, setAdresse] = useState('');
  const [urgence, setUrgence] = useState('normale');
  const [typeTravaux, setTypeTravaux] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await portalApi().post('/demandes', {
        type, titre, description, adresse, urgence, type_travaux: typeTravaux
      });
      setSent(true);
    } catch (e) {
      console.error(e);
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Demande envoyée !</h2>
        <p className="text-slate-500 mb-6">Reno Resolve va traiter votre demande et vous répondra rapidement.</p>
        <button onClick={onBack} className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">
          Retour au tableau de bord
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
          <Home className="w-5 h-5 text-slate-600" />
        </button>
        <h2 className="text-xl font-bold text-slate-900">Nouvelle demande</h2>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        {/* Type selector */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Type de demande</label>
          <div className="grid grid-cols-4 gap-2">
            {[
              { id: 'libre', label: 'Libre', icon: MessageSquare },
              { id: 'travaux', label: 'Travaux', icon: HardHat },
              { id: 'question', label: 'Question', icon: AlertTriangle },
              { id: 'document', label: 'Document', icon: FileText },
            ].map(t => (
              <button
                key={t.id} type="button"
                onClick={() => setType(t.id)}
                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                  type === t.id ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <t.icon className={`w-5 h-5 ${type === t.id ? 'text-green-600' : 'text-slate-400'}`} />
                <span className={`text-xs font-medium ${type === t.id ? 'text-green-700' : 'text-slate-600'}`}>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Titre *</label>
          <input
            value={titre} onChange={e => setTitre(e.target.value)}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
            placeholder="Ex: Demande de devis ravalement, Question sur facture..."
            required
            data-testid="demande-titre-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
          <textarea
            value={description} onChange={e => setDescription(e.target.value)}
            rows={4}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
            placeholder="Décrivez votre demande en détail..."
            required
            data-testid="demande-description-input"
          />
        </div>

        {type === 'travaux' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type de travaux</label>
                <select
                  value={typeTravaux} onChange={e => setTypeTravaux(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg"
                >
                  <option value="">Sélectionner...</option>
                  <option value="ravalement">Ravalement de façade</option>
                  <option value="peinture">Peinture</option>
                  <option value="menuiserie">Menuiserie</option>
                  <option value="plomberie">Plomberie</option>
                  <option value="electricite">Électricité</option>
                  <option value="renovation">Rénovation générale</option>
                  <option value="volet">Volets roulants</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Urgence</label>
                <select
                  value={urgence} onChange={e => setUrgence(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg"
                >
                  <option value="normale">Normale</option>
                  <option value="urgente">Urgente</option>
                  <option value="planifiee">Planifiée</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Adresse du site</label>
              <input
                value={adresse} onChange={e => setAdresse(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                placeholder="Adresse du chantier..."
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onBack} className="px-4 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50">
            Annuler
          </button>
          <button
            type="submit"
            disabled={sending || !titre.trim() || !description.trim()}
            className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
            data-testid="submit-demande-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer la demande
          </button>
        </div>
      </form>
    </div>
  );
}

// ==========================================
// MAIN PORTAL APP
// ==========================================
export default function PortailClient() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('portal_token');
    if (!token) {
      setChecking(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/portail/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem('portal_token');
    }
    setChecking(false);
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    setUser(null);
    axios.post(`${API}/api/portail/auth/logout`, {}, { withCredentials: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!user) {
    return <PortalLogin onLogin={handleLogin} />;
  }

  return (
    <PortalAuthContext.Provider value={{ user, logout: handleLogout }}>
      <PortalDashboard />
    </PortalAuthContext.Provider>
  );
}
